import { usePublicOrganizationQuery } from '@alice-financial/alice-sdk'
import {
  AliceWordmarkGreen,
  Container,
  Grid,
  Skeleton,
  SwitchLanguage,
  Typography,
} from '@alice-financial/pretext-ui'
import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import { Navigate, useParams } from 'react-router'
import { EmployerSelectionList } from './EmployerSelectionList'

export const SignupRouter = () => {
  const { orgInviteCode } = useParams<{ orgInviteCode: string }>()

  const { data, isLoading, isError } = usePublicOrganizationQuery(
    { inviteCode: orgInviteCode! },
    { enabled: Boolean(orgInviteCode) }
  )

  if (isLoading) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Skeleton variant="text" width="60%" height={40} />
        <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
      </Container>
    )
  }

  if (isError || !data?.publicOrganization) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Typography variant="h1" gutterBottom>
          <FormattedMessage id="signup.organization_not_found.heading" />
        </Typography>
        <Typography variant="body1">
          <FormattedMessage id="signup.organization_not_found.message" />
        </Typography>
      </Container>
    )
  }

  const { name, enrollmentEmployers } = data.publicOrganization

  // If there's only one employer, redirect directly to enrollment
  if (enrollmentEmployers.length === 1 && enrollmentEmployers[0]) {
    return <Navigate to={`/enroll/${enrollmentEmployers[0].inviteCode}/register`} replace />
  }

  // If there are multiple employers, show selection list
  return (
    <Grid container flexDirection="column" sx={{ p: 1, pt: 4 }} height="100%" alignItems="center">
      <Typography variant="h1" gutterBottom>
        <AliceWordmarkGreen height={32} width={200} title="Alice" />
      </Typography>
      <Typography variant="h2" gutterBottom textAlign="center" fontWeight={500} maxWidth={500} marginX="auto">
        <FormattedMessage id="enroll.landing.get_ready_to_save" />
      </Typography>
      <Container maxWidth="sm">
        <Grid
          container
          justifyContent="center"
          textAlign="center"
          marginBottom={2}
          sx={(theme) => ({
            borderTop: '1px solid',
            borderBottom: '1px solid',
            borderColor: theme.palette.divider,
          })}
        >
          <SwitchLanguage />
        </Grid>
        <Typography gutterBottom data-testid="enrollment-employer">
          <FormattedMessage id="enroll.landing.alice_has_partnered_with" values={{ employerName: name }} />
        </Typography>
        <Typography gutterBottom>
          <FormattedMessage id="signup.select_employer.heading" />
        </Typography>
        <EmployerSelectionList employers={enrollmentEmployers} />
      </Container>
    </Grid>
  )
}
